"use client";

import Link from "next/link";

import cn from "classnames";

import { toDialableNumber, toTelHref } from "@/utils/string.util";

import { useCampaign } from "@/contexts/campaignProvider.context";
import { useSettings } from "@/contexts/settingsProvider.context";

/** Click-to-call link using a normalized `tel:` URI; renders nothing when the number is too short. */
export function PhoneNumber({
  className,
  desktopClassName,
  mobileClassName,
  formatNumber,
  numberClassName,
  children,
  showNumber,
  ...rest
}: PhoneNumberProps) {
  const settings = useSettings();
  const campaign = useCampaign();
  const desktopPhoneNumber = campaign?.tfnDesktop ?? settings?.phoneNumber;
  const mobilePhoneNumber = campaign?.tfnMobile ?? settings?.phoneNumber;
  if (!desktopPhoneNumber && !mobilePhoneNumber) return null;
  return (
    <>
      {mobilePhoneNumber ? (
        <Link
          data-testid="phone-number-mobile"
          href={toTelHref(mobilePhoneNumber)}
          className={cn(className, "lg:hidden", mobileClassName ?? "inline")}
          aria-label={`Call ${mobilePhoneNumber}`}
          {...rest}>
          {children}
          {showNumber ? (
            <span className={numberClassName}>
              {formatNumber
                ? formatPhoneNumber(mobilePhoneNumber)
                : mobilePhoneNumber}
            </span>
          ) : null}
        </Link>
      ) : null}
      {desktopPhoneNumber ? (
        <Link
          data-testid="phone-number-desktop"
          href={toTelHref(desktopPhoneNumber)}
          className={cn(className, "hidden", desktopClassName ?? "lg:flex")}
          aria-label={`Call ${desktopPhoneNumber}`}
          {...rest}>
          {children}
          {showNumber ? (
            <span className={numberClassName}>
              {formatNumber
                ? formatPhoneNumber(desktopPhoneNumber)
                : desktopPhoneNumber}
            </span>
          ) : null}
        </Link>
      ) : null}
    </>
  );
}
export type PhoneNumberProps = React.AnchorHTMLAttributes<unknown> & {
  children?: React.ReactNode;
  desktopClassName?: string;
  mobileClassName?: string;
  formatNumber?: boolean;
  numberClassName?: string;
  showNumber?: boolean;
};

const formatPhoneNumber = (phoneNumber: string) => {
  const dialableNumber = toDialableNumber(phoneNumber);
  const digits =
    dialableNumber.length === 11 && dialableNumber.startsWith("1")
      ? dialableNumber.slice(1)
      : dialableNumber;

  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : phoneNumber;
};
